import { useState, useEffect, useCallback } from 'react';
import { AuthProviderProps } from './AuthProvider.types';
import { AuthContext } from './useAuthContext';
import { LocalStorageKeys, Session, UserType } from 'src/types/common';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from 'src/firebase-config';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAGS } from 'src/utils/growthBook/growthBook.constants';
import { setUser, setTag } from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';
import { App, AppInfo } from '@capacitor/app';
import { useAmplitude } from 'src/context/AmplitudeProvider';
import { useCurrentUserQuery } from 'src/features/auth/presentation/hooks/useCurrentUserQuery';
import { useQueryClient } from '@tanstack/react-query';
import { GetCurrentUserResult } from 'src/features/auth/domain/useCases/types';
import useLocalStorageState from 'src/common/hooks/useLocalStorageState';
import growthbook from 'src/utils/growthBook/growthBook';

export default function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [storageSession, setStorageSession] = useLocalStorageState(
    LocalStorageKeys.Session,
    undefined
  );
  const { currentUser: serverUser, queryKey: userQueryKey } =
    useCurrentUserQuery({
      initialUser: storageSession?.user,
    });
  const [tokenExpired, setTokenExpired] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(
    auth.currentUser
  );
  const { resetAmplitudeUser, setAmplitudeUser } = useAmplitude();

  const setSession = useCallback(
    (session: Session | undefined | null) => {
      const user = session?.user;
      if (user) {
        setStorageSession({ ...session, user });
        queryClient.setQueryData<GetCurrentUserResult>(userQueryKey, user);

        setUser({
          id: user.pk.toString(),
          email: user.email,
        });

        setAmplitudeUser({
          email: user.email,
          phone: user.phone_number,
          name: user.first_name,
          last_name: user.last_name,
          user_id: user.pk,
          is_b2b: user.type === UserType.B2b,
          document_number: user.profile?.document_number,
          document_type: user.profile?.document_type,
        });

        Promise.resolve(Capacitor.getPlatform())
          .then((platform: string) => {
            if (platform !== 'web') {
              return App.getInfo();
            }

            return Promise.resolve({ version: 'web', build: '' });
          })
          .then((info: Pick<AppInfo, 'build' | 'version'>) => {
            return setTag(
              'appVersion',
              `${info.version}${info.build ? `-${info.build}` : ''}`
            );
          });
      } else {
        setStorageSession(undefined);
        queryClient.removeQueries();
        resetAmplitudeUser();
        if (firebaseUser) {
          auth.signOut();
        }
      }
    },

    [
      firebaseUser,
      queryClient,
      userQueryKey,
      setAmplitudeUser,
      resetAmplitudeUser,
      setStorageSession,
    ]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setFirebaseUser(user);
      } else {
        setFirebaseUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (tokenExpired) {
      setTokenExpired(false);
      serverUser && setSession(null);
      resetAmplitudeUser();
      growthbook.setAttributes({ id: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenExpired, setSession, setTokenExpired]);

  useEffect(() => {
    if (serverUser?.updated && storageSession) {
      const { updated: _updated, ...updatedUser } = serverUser;

      setSession({ ...storageSession, user: updatedUser });
    }

    growthbook.setAttributes({ id: serverUser?.pk });
  }, [serverUser, storageSession, setSession]);

  const isB2BFeatureEnabled = useFeatureIsOn(FEATURE_FLAGS.ENABLE_B2B);

  const isLoggedIn = !!serverUser && !!storageSession?.accessToken;

  const session = isLoggedIn
    ? { user: serverUser, accessToken: storageSession.accessToken }
    : null;

  return (
    <AuthContext.Provider
      value={{
        session,
        setSession,
        user: session?.user,
        firebaseUser,
        isB2b:
          isB2BFeatureEnabled && isLoggedIn
            ? serverUser.type === UserType.B2b
            : false,
        tokenExpired,
        setTokenExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
